import type { TFunction } from 'i18next';
import intersection from 'lodash/intersection';
import type {
  IPaymentCard,
  ListPaymentInfosResponse,
  PaymentMethod,
  ISelectOption,
  ListBookingPaymentSourcesResponse,
  BookingPaymentSource,
  Payment,
} from '../types';
import { ManualFormPaymentType, PaymentMethodEnum, PaymentSourceType } from '../types';
import { CreditCardAccessType } from '../types/api/v2/obt/model/credit-card-access-type';
import { DELAYED_INVOICE_FAKE_CARD_LABEL, carVendors } from '../constants';
import { isExpiredPaymentCard, maskCardNumber, sentenceCase } from './common';
import type { PaymentInfo, PaymentInfoApplicableToEnum } from '../types/api/v2/obt/model/payment-info';
import { FormOfPaymentTypeEnum } from '../types/api/v2/obt/model/form-of-payment';
import { PaymentMethod as PaymentMethodV2 } from '../types/api/v2/obt/model/payment-method';
import type { Card as CardV1 } from '../types/api/v1/common/card';
import {
  CardCompany as CardCompanyEnumV1,
  CardType as CardTypeEnumV1,
  OwnershipLabel,
} from '../types/api/v1/common/card';
import type { Card } from '../types/api/v2/obt/model/card';
import { CardOwnershipLabelEnum, CardTypeEnum } from '../types/api/v2/obt/model/card';
import { CardCompany } from '../types/api/v2/obt/model/card-company';
import { defineCommonMessage, defineMessage } from '../translations/defineMessage';
import { PolicyPredicate } from '../types/api/v2/obt/model/policy-predicate';
import type { CustomPaymentMethodMetadataWrapper } from '../types/api/v2/obt/model/custom-payment-method-metadata-wrapper';
import type { BrexBudgetMetadataWrapper } from '../types/api/v2/obt/model/brex-budget-metadata-wrapper';

export const centralCardFilter = (card: IPaymentCard): boolean =>
  card.cardAccessType === CreditCardAccessType.Centralised;

export const tmcCardFilter = (card: IPaymentCard): boolean => card.cardAccessType === CreditCardAccessType.Tmc;

export type PaymentOptionType = {
  type?: ManualFormPaymentType | null;
  paymentCard?: PaymentInfo['card'] | null;
};

export const mapManualFormPaymentTypeToRequest = (formOfPayment: ManualFormPaymentType): FormOfPaymentTypeEnum => {
  switch (formOfPayment) {
    case ManualFormPaymentType.CARD:
    case ManualFormPaymentType.CENTRAL:
    case ManualFormPaymentType.PERSONAL:
    case ManualFormPaymentType.TMC:
    case ManualFormPaymentType.BREX_BUDGET:
    case ManualFormPaymentType.DIRECT_BILL:
      return FormOfPaymentTypeEnum.Card;

    case ManualFormPaymentType.CHEQUE:
      return FormOfPaymentTypeEnum.Cheque;
    case ManualFormPaymentType.TF_PAY:
      return FormOfPaymentTypeEnum.Tfpay;
    case ManualFormPaymentType.CASH:
      return FormOfPaymentTypeEnum.Cash;

    case ManualFormPaymentType.BREX_POINTS:
      return FormOfPaymentTypeEnum.BrexPoints;

    default:
      throw new Error(`Unsupported form of payment: ${formOfPayment}`);
  }
};

const mapCardAccessTypeToPaymentType = (accessType?: CreditCardAccessType): ManualFormPaymentType => {
  switch (accessType) {
    case CreditCardAccessType.Centralised:
      return ManualFormPaymentType.CENTRAL;
    case CreditCardAccessType.Tmc:
      return ManualFormPaymentType.TMC;
    case CreditCardAccessType.Personal:
      return ManualFormPaymentType.PERSONAL;
    default:
      throw new Error(`Unknown credit card access type "${accessType}"`);
  }
};

export const mapManualFormPaymentTypeToCardAccessType = (
  formOfPayment: ManualFormPaymentType,
): CreditCardAccessType => {
  switch (formOfPayment) {
    case ManualFormPaymentType.CENTRAL:
      return CreditCardAccessType.Centralised;
    case ManualFormPaymentType.PERSONAL:
      return CreditCardAccessType.Personal;
    case ManualFormPaymentType.TMC:
      return CreditCardAccessType.Tmc;
    default:
      return CreditCardAccessType.UnknownType;
  }
};

export const isCardFormOfPayment = (fop: ManualFormPaymentType): boolean =>
  [
    ManualFormPaymentType.PERSONAL,
    ManualFormPaymentType.TMC,
    ManualFormPaymentType.CARD,
    ManualFormPaymentType.CENTRAL,
    ManualFormPaymentType.BREX_BUDGET,
    ManualFormPaymentType.DIRECT_BILL,
  ].includes(fop);

interface GetInfoLabelProps {
  translator: TFunction;
  paymentType: ManualFormPaymentType;
  card?: PaymentInfo['card'] | null;
  disabled?: boolean;
  budgetName?: string;
  isCardOwnershipLabelEnabled?: boolean;
}

export const getPaymentInfoLabel = ({
  translator,
  paymentType,
  card,
  disabled,
  budgetName,
  isCardOwnershipLabelEnabled,
}: GetInfoLabelProps): ISelectOption<PaymentOptionType> => {
  const cardNumber = card?.number?.slice(-4);
  const cardLabel = card?.label ?? '';
  const ownershipLabel = card?.ownershipLabel ? sentenceCase(card.ownershipLabel) : '';
  const showOwnershipLabel = isCardOwnershipLabelEnabled && card?.ownershipLabel;

  let { expiryMonth, expiryYear } = card ?? {};
  if (card?.expiry) {
    expiryMonth = 'expiry' in card.expiry ? card.expiry.expiry?.expiryMonth : undefined;
    expiryYear = 'expiry' in card.expiry ? card.expiry.expiry?.expiryYear : undefined;
  }
  const isExpired = !!card && !!expiryMonth && !!expiryYear && isExpiredPaymentCard(expiryMonth, expiryYear);
  const isDisabled = disabled;
  switch (paymentType) {
    case ManualFormPaymentType.CASH: {
      return {
        value: {
          type: ManualFormPaymentType.CASH,
        },
        label: translator(defineMessage('Cash')),
      };
    }
    case ManualFormPaymentType.CHEQUE: {
      return {
        value: {
          type: ManualFormPaymentType.CHEQUE,
        },
        label: translator(defineMessage('Check or Wire')),
      };
    }
    case ManualFormPaymentType.TF_PAY: {
      return {
        value: {
          type: ManualFormPaymentType.TF_PAY,
        },
        label: translator(defineMessage('TF Pay')),
      };
    }
    case ManualFormPaymentType.CENTRAL: {
      return {
        value: {
          type: ManualFormPaymentType.CENTRAL,
          paymentCard: card,
        },
        label: translator(defineMessage('Central Card (xxxx {{cardNumber}} {{cardLabel}})'), {
          cardNumber,
          cardLabel,
        }),
        isExpired,
        isDisabled,
      };
    }
    case ManualFormPaymentType.TMC: {
      return {
        value: {
          type: ManualFormPaymentType.TMC,
          paymentCard: card,
        },
        label:
          cardLabel === DELAYED_INVOICE_FAKE_CARD_LABEL
            ? cardLabel
            : translator(defineMessage('TMC Card (xxxx {{cardNumber}} {{cardLabel}})'), { cardNumber, cardLabel }),
        isExpired,
        isDisabled,
      };
    }
    case ManualFormPaymentType.PERSONAL: {
      return {
        value: {
          type: ManualFormPaymentType.PERSONAL,
          paymentCard: card,
        },
        label: showOwnershipLabel
          ? translator(defineMessage('Traveler {{ownershipLabel}} Card (xxxx {{cardNumber}} {{cardLabel}})'), {
              cardNumber,
              cardLabel,
              ownershipLabel,
            })
          : translator(defineMessage('Traveler Card (xxxx {{cardNumber}} {{cardLabel}})'), {
              cardNumber,
              cardLabel,
            }),
        isExpired,
        isDisabled,
      };
    }
    case ManualFormPaymentType.CARD: {
      return {
        value: {
          type: ManualFormPaymentType.CARD,
        },
        label: showOwnershipLabel
          ? translator(defineMessage('{{ownershipLabel}} Card (xx {{cardNumber}}) {{cardLabel}}'), {
              cardNumber,
              cardLabel,
              ownershipLabel,
            })
          : `(xx ${cardNumber}) ${cardLabel}`,
        isExpired,
        isDisabled,
      };
    }
    case ManualFormPaymentType.BREX_POINTS:
      return {
        value: {
          type: ManualFormPaymentType.BREX_POINTS,
        },
        label: translator(defineMessage('Brex Points')),
        isExpired,
        isDisabled,
      };
    case ManualFormPaymentType.BREX_BUDGET:
      return {
        value: {
          type: ManualFormPaymentType.BREX_BUDGET,
          paymentCard: card,
        },
        label: translator(defineMessage('Brex Budget ({{budgetName}})'), { budgetName }),
        isExpired,
        isDisabled,
      };
    case ManualFormPaymentType.DIRECT_BILL: {
      return {
        value: {
          type: ManualFormPaymentType.DIRECT_BILL,
          paymentCard: card,
        },
        label: translator(defineMessage('Direct bill (xxxx {{cardNumber}} - {{cardLabel}})'), {
          cardNumber,
          cardLabel,
        }),
        isExpired,
        isDisabled,
      };
    }
    default:
      throw new Error(`Unsupported payment info "${paymentType}"`);
  }
};

export const isPaymentAddOnViolation = (paymentSource: BookingPaymentSource | undefined): boolean => {
  return (
    paymentSource?.paymentPolicyInfo?.ruleResultInfos?.some((ruleInfo) =>
      ruleInfo.violationInfos?.some((violation) => violation.predicate === PolicyPredicate.PaymentAirAddonViolation),
    ) ?? false
  );
};

interface IGetPaymentMethodOptions {
  paymentInfos: ListPaymentInfosResponse['paymentInfos'] | undefined;
  bookingPaymentSources: ListBookingPaymentSourcesResponse['bookingPaymentSources'] | undefined;
  translator: TFunction;
  accessTypes: CreditCardAccessType[];
  applicableTo: PaymentInfoApplicableToEnum[];
  includeCheque?: boolean;
  includeTfPay?: boolean;
  includeCash?: boolean;
  isDirectBillingEnabled?: boolean;
  selectedCarVendorCode?: string;
  isCardOwnershipLabelEnabled: boolean;
}

export const getPaymentMethodOptions = ({
  paymentInfos,
  bookingPaymentSources,
  translator,
  accessTypes,
  applicableTo = [],
  includeCheque = true,
  includeTfPay = true,
  includeCash,
  isDirectBillingEnabled = false,
  selectedCarVendorCode,
  isCardOwnershipLabelEnabled,
}: IGetPaymentMethodOptions): ISelectOption<PaymentOptionType>[] => [
  ...(bookingPaymentSources
    ?.filter(
      (bookingPaymentSource) =>
        isDirectBillingEnabled &&
        bookingPaymentSource.type === PaymentSourceType.VendorProgramPayment &&
        selectedCarVendorCode &&
        bookingPaymentSource.metadata &&
        'vendorProgramPaymentDescriptor' in bookingPaymentSource.metadata &&
        selectedCarVendorCode === bookingPaymentSource.metadata.vendorProgramPaymentDescriptor?.vendorInfo?.vendorId,
    )
    .map((bookingPaymentSource) => {
      // This if is added only for type safety
      if (
        bookingPaymentSource.metadata &&
        'vendorProgramPaymentDescriptor' in bookingPaymentSource.metadata &&
        bookingPaymentSource.metadata.vendorProgramPaymentDescriptor?.vendorProgramPaymentMetadata &&
        'directBilling' in bookingPaymentSource.metadata.vendorProgramPaymentDescriptor.vendorProgramPaymentMetadata
      ) {
        const { vendorProgramPaymentMetadata, vendorInfo } =
          bookingPaymentSource.metadata?.vendorProgramPaymentDescriptor;

        return getPaymentInfoLabel({
          translator,
          paymentType: ManualFormPaymentType.DIRECT_BILL,
          card: {
            id: bookingPaymentSource.id,
            number: vendorProgramPaymentMetadata.directBilling?.directBillingCode ?? '',
            label: vendorInfo?.vendorName,
          },
          isCardOwnershipLabelEnabled,
        });
      }

      return getPaymentInfoLabel({
        translator,
        paymentType: ManualFormPaymentType.DIRECT_BILL,
        card: {
          id: bookingPaymentSource.id,
          number: '',
          label: '',
        },
        isCardOwnershipLabelEnabled,
      });
    }) ?? []),
  ...(bookingPaymentSources
    ?.filter((bookingPaymentSource) => bookingPaymentSource.type === PaymentSourceType.CustomPaymentMethod)
    .map((bookingPaymentSource) =>
      getPaymentInfoLabel({
        translator,
        paymentType: ManualFormPaymentType.BREX_BUDGET,
        card: { id: bookingPaymentSource.id, cvv: '', number: '' },
        budgetName: (
          (bookingPaymentSource.metadata as CustomPaymentMethodMetadataWrapper)
            ?.customPaymentMethodMetadata as BrexBudgetMetadataWrapper
        )?.brexBudgetMetadata.budgetName,
        isCardOwnershipLabelEnabled,
      }),
    ) ?? []),
  ...(paymentInfos
    ?.filter(
      (paymentInfo) =>
        paymentInfo?.access?.accessType &&
        accessTypes.includes(paymentInfo.access.accessType) &&
        (paymentInfo?.applicableTo?.length && applicableTo?.length
          ? intersection(paymentInfo?.applicableTo, applicableTo)
          : true),
    )
    .map((paymentInfo) =>
      getPaymentInfoLabel({
        translator,
        paymentType: mapCardAccessTypeToPaymentType(paymentInfo?.access?.accessType),
        card: paymentInfo.card,
        isCardOwnershipLabelEnabled,
      }),
    ) ?? []),
  ...(bookingPaymentSources
    ?.filter(
      (bookingPaymentSource) =>
        bookingPaymentSource.type === PaymentSourceType.Card &&
        bookingPaymentSource.paymentAccess?.creditCardAccess.accessType &&
        accessTypes.includes(bookingPaymentSource.paymentAccess?.creditCardAccess.accessType),
    )
    .map((bookingPaymentSource) =>
      getPaymentInfoLabel({
        translator,
        paymentType: mapCardAccessTypeToPaymentType(bookingPaymentSource.paymentAccess?.creditCardAccess.accessType),
        card:
          bookingPaymentSource?.metadata && 'cardMetadata' in bookingPaymentSource?.metadata
            ? bookingPaymentSource?.metadata.cardMetadata.card
            : null,
        disabled: isPaymentAddOnViolation(bookingPaymentSource),
        isCardOwnershipLabelEnabled,
      }),
    ) ?? []),
  ...(includeCash ? [getPaymentInfoLabel({ translator, paymentType: ManualFormPaymentType.CASH })] : []),
  ...(includeCheque ? [getPaymentInfoLabel({ translator, paymentType: ManualFormPaymentType.CHEQUE })] : []),
  ...(includeTfPay ? [getPaymentInfoLabel({ translator, paymentType: ManualFormPaymentType.TF_PAY })] : []),
];

interface GetPaymentOptionProps {
  translator: TFunction;
  paymentType?: ManualFormPaymentType | null;
  card?: PaymentInfo['card'] | null;
  budgetName?: string;
  isCardOwnershipLabelEnabled?: boolean;
}

export const getPaymentOption = ({
  translator,
  paymentType,
  card,
  budgetName,
  isCardOwnershipLabelEnabled,
}: GetPaymentOptionProps): ISelectOption<PaymentOptionType> => {
  if (!paymentType) {
    return {
      label: card ? `${maskCardNumber(String(card.number))} ${card.label ?? ''}` : '',
      value: {
        type: null,
        paymentCard: card,
      },
    };
  }

  return getPaymentInfoLabel({ translator, paymentType, card, budgetName, isCardOwnershipLabelEnabled });
};

export const convertV1CardToV2Card = (v1Card: CardV1): Card => {
  const v1Tov2CardCompanyMap = new Map();
  v1Tov2CardCompanyMap.set(CardCompanyEnumV1.AMEX, CardCompany.Amex);
  v1Tov2CardCompanyMap.set(CardCompanyEnumV1.AIR_TRAVEL_UATP, CardCompany.AirTravelUatp);
  v1Tov2CardCompanyMap.set(CardCompanyEnumV1.CARTE_BLANCHE, CardCompany.CarteBlanche);
  v1Tov2CardCompanyMap.set(CardCompanyEnumV1.DINERS_CLUB, CardCompany.DinersClub);
  v1Tov2CardCompanyMap.set(CardCompanyEnumV1.DISCOVER, CardCompany.Discover);
  v1Tov2CardCompanyMap.set(CardCompanyEnumV1.JCB, CardCompany.Jcb);
  v1Tov2CardCompanyMap.set(CardCompanyEnumV1.MASTERCARD, CardCompany.Mastercard);
  v1Tov2CardCompanyMap.set(CardCompanyEnumV1.VISA, CardCompany.Visa);

  const v1Tov2CardTypeMap = new Map();
  v1Tov2CardTypeMap.set(CardTypeEnumV1.CREDIT, CardTypeEnum.Credit);
  v1Tov2CardTypeMap.set(CardTypeEnumV1.DEBIT, CardTypeEnum.Debit);

  const v1Tov2CardOwnershipLabelMap = new Map();
  v1Tov2CardOwnershipLabelMap.set(OwnershipLabel.CENTRAL, CardOwnershipLabelEnum.Central);
  v1Tov2CardOwnershipLabelMap.set(OwnershipLabel.CORPORATE, CardOwnershipLabelEnum.Corporate);
  v1Tov2CardOwnershipLabelMap.set(OwnershipLabel.PERSONAL, CardOwnershipLabelEnum.Personal);

  return {
    ...v1Card,
    ownershipLabel: v1Tov2CardOwnershipLabelMap.get(v1Card.ownershipLabel) ?? undefined,
    company: v1Tov2CardCompanyMap.get(v1Card.company) ?? CardCompany.None,
    type: v1Tov2CardTypeMap.get(v1Card.type) ?? CardTypeEnum.Unknown,
  };
};

export const isValidPaymentMethod = (paymentMethod?: string | number | undefined): paymentMethod is PaymentMethod => {
  if (Number.isNaN(paymentMethod) || typeof paymentMethod === 'undefined' || paymentMethod === null) return false;

  return Object.values(PaymentMethodEnum).includes(+paymentMethod);
};

export const mapPaymentSourceTypeToPaymentMethodEnum = (paymentSourceType: PaymentSourceType): PaymentMethodV2 => {
  // For brex rewards, the value for paymentMethods enum is BREX_POINTS and for all other payment sources it's CREDIT_CARD
  if (paymentSourceType === PaymentSourceType.RewardsProgram) {
    return PaymentMethodV2.BrexPoints;
  }
  return PaymentMethodV2.CreditCard;
};

export const ADDON_PAYMENT_VIOLATION_TEXT = defineCommonMessage(
  'Please be aware that your company policy does not permit the use of this payment method for bookings with add-ons.',
);

export const CARD_EXPIRED_TEXT = defineCommonMessage('Your card on file has expired');

export function getVendorProgramPaymentCard(goodsSoldPayment: Payment): Card {
  // This if is added only for type safety, the else blocked will never be reached
  if (
    goodsSoldPayment.fop?.paymentMetadata?.vendorProgramPaymentMetadata &&
    'directBilling' in goodsSoldPayment.fop?.paymentMetadata?.vendorProgramPaymentMetadata
  ) {
    const { directBilling } = goodsSoldPayment.fop.paymentMetadata?.vendorProgramPaymentMetadata;
    return {
      id: goodsSoldPayment.paymentId,
      number: directBilling?.directBillingCode ?? '',
      label: carVendors[directBilling?.vendorId ?? ''],
    };
  }

  return {
    id: goodsSoldPayment.paymentId,
    number: '',
    label: '',
  };
}
